import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { BRAND } from "../../lib/constants";
import { useLang } from "../../lib/lang";

// Match the "is this a touch / coarse-pointer device with limited bandwidth?" definition
// used elsewhere in the app. Computed ONCE per app boot so it never re-renders.
const IS_MOBILE = typeof window !== "undefined"
  && (window.matchMedia?.("(max-width: 768px)").matches
      || window.matchMedia?.("(pointer: coarse)").matches);

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoIndex, setVideoIndex] = useState(0);
  const [posterIdx, setPosterIdx] = useState(0);
  // On mobile we ship a single poster image and SKIP the video entirely — the
  // autoplay <video> is ~3 MB and was the dominant source of the 8.8 s mobile
  // LCP. Posters are 200-500 KB each.
  const [renderVideo] = useState(!IS_MOBILE);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacityText = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const scaleVideo = useTransform(scrollYProgress, [0, 1], [1.04, 1.18]);

  // Reload the <video> element whenever we cycle to a new src on error.
  useEffect(() => {
    if (renderVideo && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [videoIndex, renderVideo]);

  // Auto-cycle the static hero poster behind the video every 5s. One-time mount.
  // Skipped on mobile to save CPU + bandwidth (one poster, no cross-fade).
  useEffect(() => {
    if (IS_MOBILE) return undefined;
    const id = setInterval(() => setPosterIdx((i) => (i + 1) % BRAND.heroPosters.length), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#0A0A0A] grain flex flex-col min-h-[760px] md:min-h-screen"
      style={{ minHeight: "100svh" }}
    >
      {/* Background media — absolute, behind all content */}
      <motion.div style={{ scale: scaleVideo }} className="absolute inset-0 z-0 pointer-events-none">
        {BRAND.heroPosters.map((p, i) => (
          <img
            key={p}
            src={`${p}?w=1600`}
            srcSet={`${p}?w=800 800w, ${p}?w=1200 1200w, ${p}?w=1600 1600w`}
            sizes="100vw"
            alt=""
            width={1920}
            height={1080}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1500ms] ${
              i === posterIdx ? "opacity-90" : "opacity-0"
            }`}
          />
        ))}
        {renderVideo && (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            loop
            preload="metadata"
            poster={BRAND.heroPosters[0]}
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-95"
            onError={() => setVideoIndex((i) => (i + 1) % BRAND.heroVideos.length)}
          >
            <source src={BRAND.heroVideos[videoIndex]} type="video/mp4" />
          </video>
        )}
      </motion.div>

      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/45 via-black/30 to-black/85 pointer-events-none" />
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/50 via-transparent to-transparent pointer-events-none" />

      {/* SPACER — reserves space for the fixed Navbar so the tagline strip never tucks under it.
          Navbar height: h-20 (5rem) mobile, h-24 (6rem) desktop. Spacer matches. */}
      <div aria-hidden="true" className="relative z-10 h-20 md:h-24 shrink-0" />

      {/* Tagline band — IN NORMAL FLOW (no longer absolute). The H1 below cannot overlap it. */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="relative z-20 shrink-0 backdrop-blur-md bg-[#0A0A0A]/55 border-y border-[#D7CFC4]/25"
        data-testid="hero-tagline-strip"
      >
        <div className="py-3 md:py-4 px-6 md:px-12 lg:px-20 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-6">
          <p className="text-[#F5F2EB] font-display text-sm sm:text-base md:text-[20px] lg:text-[22px] leading-tight tracking-wide text-center md:text-left">
            {t("tagline.text")} <em className="text-[#B08858] not-italic font-semibold">{t("tagline.solutions")}</em>
          </p>
          {/* Category chip row — desktop/tablet only; mobile keeps the strip compact (one line) */}
          <div className="hidden sm:flex flex-wrap items-center justify-center md:justify-end gap-x-2 md:gap-x-3 gap-y-1 text-[10px] md:text-[12px] lg:text-[13px] text-[#F5F2EB] font-medium tracking-[0.14em] uppercase">
            {["tagline.kitchen","tagline.bathroom","tagline.bedroom","tagline.tv","tagline.doors","tagline.furniture"].map((key, i, arr) => (
              <React.Fragment key={key}>
                <span className="whitespace-nowrap">{t(key)}</span>
                {i < arr.length - 1 && <span className="text-[#B08858]/60 text-sm leading-none">|</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Value-prop "what's included" ribbon — strategically placed between the
          tagline strip and the H1 so EVERY visitor sees our service deliverables
          in the first viewport. Charcoal + cream + wood accent for luxury feel. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.55 }}
        className="relative z-20 shrink-0 bg-[#1A1A1A] text-[#F5F2EB] border-b border-[#8B5E3C]/40"
        data-testid="hero-offer-strip"
      >
        <div className="px-6 md:px-12 lg:px-20 py-2.5 md:py-3 flex flex-nowrap overflow-x-auto md:flex-wrap items-center justify-start md:justify-center gap-x-3 md:gap-x-5 gap-y-1 text-[10px] md:text-[12px] tracking-[0.14em] uppercase font-semibold">
          <span className="hidden md:inline-block text-[#B08858] whitespace-nowrap">
            {t("offer.label")} ·
          </span>
          {["offer.software","offer.design","offer.rendering","offer.production","offer.delivery","offer.install"].map((key, i, arr) => (
            <React.Fragment key={key}>
              <span className="whitespace-nowrap flex items-center gap-1.5">
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="shrink-0"><path d="M1 5l3 3 5-6" stroke="#B08858" strokeWidth="1.8" fill="none" strokeLinecap="square" /></svg>
                {t(key)}
              </span>
              {i < arr.length - 1 && <span className="text-[#F5F2EB]/25 leading-none hidden md:inline">·</span>}
            </React.Fragment>
          ))}
        </div>
      </motion.div>

      {/* Main content — IN NORMAL FLOW below the tagline. Grows to fill the remaining viewport. */}
      <motion.div
        style={{ y: yText, opacity: opacityText }}
        className="relative z-10 flex-1 flex flex-col justify-end pt-10 pb-10 sm:pt-14 md:pt-16 md:pb-24 px-6 md:px-12 lg:px-20"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="overline text-[#B08858] mb-4 md:mb-5"
          data-testid="hero-overline"
        >
          {t("hero.overline")}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.15 }}
          className="font-display text-[#F5F2EB] text-[36px] leading-[1.05] sm:text-5xl md:text-7xl lg:text-8xl xl:text-[104px] md:leading-[1.0] tracking-tight"
          data-testid="hero-headline"
        >
          <span>{t("hero.headline_1")}</span>{" "}
          <em className="not-italic text-[#B08858]">{t("hero.headline_em")}</em>{" "}
          <br />
          <span>{t("hero.headline_2")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-5 md:mt-8 text-[#F5F2EB]/85 text-sm sm:text-lg md:text-xl leading-relaxed tracking-wide max-w-[920px]"
          data-testid="hero-subhead"
        >
          {t("hero.subhead")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-6 md:mt-10 flex flex-wrap gap-3 sm:gap-4"
        >
          <Link to="/consultation" data-testid="hero-cta-consultation" className="btn-sharp primary group">
            {t("hero.cta_consult")}
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/products" data-testid="hero-cta-quote" className="btn-sharp outline-light">
            {t("hero.cta_quote")}
          </Link>
          <Link to="/showroom" data-testid="hero-cta-showroom" className="btn-sharp outline-light">
            {t("hero.cta_showroom")}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ delay: 1.2, duration: 2, repeat: Infinity }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[#F5F2EB]/70 flex flex-col items-center gap-2 text-[10px] tracking-[0.3em] uppercase z-10"
      >
        {t("common.scroll")} <ChevronDown size={16} />
      </motion.div>
    </section>
  );
}
